import React, { useState } from 'react';
import { Box, Modal, IconButton, Typography, useTheme } from '@mui/material';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import CloseIcon from '@mui/icons-material/Close';

interface ChatImageProps {
  src: string;
  alt: string;
  caption?: string;
  maxWidth?: string | number;
}

const ChatImage: React.FC<ChatImageProps> = ({ 
  src, 
  alt, 
  caption, 
  maxWidth = 300 
}) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Box 
      sx={{
        my: 2,
        position: 'relative',
        maxWidth: maxWidth,
        '&:hover .zoom-icon': {
          opacity: 1,
        }
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: '100%',
          height: 'auto',
          borderRadius: 2,
          cursor: 'pointer',
          boxShadow: theme.shadows[2],
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: theme.shadows[4],
          },
        }}
        onClick={handleOpen}
      />
      
      <IconButton
        className="zoom-icon"
        onClick={handleOpen}
        size="small"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          color: 'white',
          opacity: 0,
          transition: 'opacity 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          },
        }}
        aria-label="Zoom image"
      >
        <ZoomOutMapIcon fontSize="small" />
      </IconButton>

      {caption && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1,
            textAlign: 'center',
            color: theme.palette.text.secondary,
          }}
        >
          {caption}
        </Typography>
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="image-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box sx={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: -16,
              top: -16,
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[2],
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
            aria-label="Close image"
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={src}
            alt={alt}
            sx={{
              maxWidth: '100%',
              maxHeight: '85vh',
              objectFit: 'contain',
              borderRadius: 2,
              boxShadow: theme.shadows[10],
            }}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default ChatImage;